import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import {
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Paper, // Import Paper for the box
  List, // Import List for the options
  ListItem,
  Box,
  IconButton, // Import ListItem for each option
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useAutocompleteSuggestions } from '@/hooks/useOnboarding'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import useOnboardingContext from '@/hooks/useOnboardingContext'
import ClearIcon from "@mui/icons-material/Clear";
import { useGetPlaceDetails } from '@/hooks/useOnboarding'

const AddressAutocomplete = ({
  register,
  name,
  validation = {},
  autoFocus,
  setValue,
  value,
  onPlaceSelected
}) => {
  const { onboardingStateSessionId } = useOnboardingContext()

  const [options, setOptions] = useState([])
  const [query, setQuery] = useState(value || '')
  const [inputValue, setInputValue] = useState(value || '')
  const [currentSuggestions, setCurrentSuggestions] = useState([])
  const [isFocused, setIsFocused] = useState(false)

  const { data: suggestions, isFetching, isFetched } = useAutocompleteSuggestions(query, onboardingStateSessionId)
  const { mutateAsync: getPlaceDetails } = useGetPlaceDetails()

  const debouncedSetQuery = useMemo(() => {
    return debounce((value) => {
      setQuery(value)
    }, 300)
  }, [])

  const highlightMatch = (text, query) => {
    if (!query) return text
  
    const words = query.trim().split(/\s+/).filter(Boolean)
    const regex = new RegExp(`(${words.join("|")})`, "gi")
  
    return text.replace(regex, `<span style="font-weight: bold;">$1</span>`)
  };

  useEffect(() => {
    const predictions = suggestions?.suggestions?.map(({placePrediction: { placeId, text: { text } }}) => ({
      Place_Id: placeId,
      Description: text
    }))
    
    setCurrentSuggestions([
      ...(predictions ?? [])
    ])
  }, [isFetched])

  useEffect(() => {
    register(name, validation)
  }, [register, name, validation])

  useEffect(() => {
    setOptions([...currentSuggestions])
  }, [currentSuggestions])

  const handleInputChange = (event) => {
    const newValue = event.target.value
    setInputValue(newValue)

    if(!newValue) setOptions([])

    setValue(`${name}_input`, newValue, { shouldValidate: true })

    setValue(name, {}, {
      shouldValidate: true
    })

    debouncedSetQuery(newValue)

    onPlaceSelected({ zipcode: null, Description: null, Place_Id: null})
  }

  const handlePlaceSelection = async (option) => {
    setInputValue(option.Description)
    setValue(`${name}_input`, option.Description, { shouldValidate: true })
    setOptions([])
    setValue(name, option, { shouldValidate: true })
    const placeDetails = await getPlaceDetails({ 
      placeId: option.Place_Id,
      sessionId: onboardingStateSessionId
    })

    if(!isEmpty(placeDetails))
      onPlaceSelected({ zipcode: placeDetails.zipcode, ...option })
  }
  
  return (
    <>
      <Box className="relative">
        <TextField
          {...register(`${name}_input`, {
            required: true
          })}
          autoFocus={autoFocus}
          variant='outlined'
          placeholder='742 Evergreen Terrace, Springfield, IL USA'
          fullWidth
          onFocus={(e) => {
            const val = e.target.value
            e.target.value = ''
            e.target.value = val
            setIsFocused(true)
          }}
          onBlur={() => setIsFocused(false)}
          value={inputValue}
          onChange={handleInputChange}
          autoComplete="off"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:focus-within svg': {
                color: 'primary.main',
              },
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  {isFetching && <CircularProgress size={25} />}
                  {!isFetching && !!inputValue.length && (
                    <IconButton
                      onClick={() => handleInputChange({ target: { value: "" } })}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" sx={(theme) => ({
                        fill: theme.palette.secondary.main,
                        fontSize: "1rem"
                      })}/>
                    </IconButton>
                  )}
                </>
              ),
              startAdornment: (
                <InputAdornment position='start'>
                  <PlaceOutlinedIcon sx={{
                    marginRight: -2
                  }}/>
                </InputAdornment>
              ),
            },
          }}
        />
        {isFocused && options.length > 0 && (
        <Paper 
          elevation={3} 
          sx={{ 
            marginTop: 1,
            maxHeight: 170,
            minWidth: "100%",
            maxWidth: "100%",
            overflowY: 'auto',
            overflowX: 'hidden',
            position: "absolute",
            zIndex: 1000,
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#999",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}>
          <List>
            {options.map((option, index) => (
              <ListItem
                key={index}
                className='p-1 hover:bg-gray-200 cursor-pointer'
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => { handlePlaceSelection(option) }}
              >
                <Box className="flex gap-x-2 items-center">
                  <PlaceOutlinedIcon fontSize='14' />
                  <Typography
                    variant='h6'
                    fontWeight={400}
                    sx={{
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightMatch(option.Description, inputValue),
                    }}
                  >
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
          <Typography
                variant='subtitle2'
                fontSize={10}
                textAlign={'right'}
                width={'100%'}
                sx={{ color: 'gray', paddingRight: 2, paddingBottom: 2 }}
              >
                Powered By Google
            </Typography>
        </Paper>
      )}
    </Box>
    </>
  )
}

export default AddressAutocomplete
